import { useState } from 'react'
import { Button, ChipInput, FormField, FormSection } from '../common'
import CryptoDotComContractSelector from '../CryptoDotComContractSelector'
import KambiOutcomeSelector from '../KambiOutcomeSelector'
import { ROLES } from '../../constants/roles'
import { LegSearch } from '../leg-search'
import OGContractSelector from '../OGContractSelector'
import {
  authoredWagerType,
  changeAuthoredWagerType,
  createAuthoredWagerEntry,
  KAMBI_WAGER_TYPES,
  estimateParlayPrice,
} from './postComposerModel'
import { kambiOutcomeLabel } from './kambiOutcomePreview'
import styles from './PostComposer.module.css'

function WagerStructureEditor({
  outcomeIds,
  entries,
  onChange,
  onOutcomeIdsChange,
  validationResult,
  validationError,
}) {
  if (!outcomeIds.length) return null

  const updateEntry = (entryIndex, update) => {
    onChange(entries.map((entry, index) => (index === entryIndex ? update(entry) : entry)))
  }
  const addWager = () => {
    const type = outcomeIds.length > 1 ? 'combination' : 'single'
    onChange([...entries, createAuthoredWagerEntry(type, outcomeIds, entries)])
  }
  const outcomeDetail = (outcomeId) => {
    const outcome = validationResult?.outcomes?.[String(outcomeId)]
    return {
      selection: kambiOutcomeLabel(outcome) || `Outcome ID ${outcomeId}`,
      event: outcome?.event_name || '',
      market: outcome?.criterion || outcome?.bet_offer_type || '',
      odds: outcome?.odds_american || '',
    }
  }
  const outcomeLabel = (outcomeId) => {
    const detail = outcomeDetail(outcomeId)
    return [detail.selection, detail.event].filter(Boolean).join(' · ')
  }
  const wagerLabel = (entry) => (
    KAMBI_WAGER_TYPES.find((option) => option.value === authoredWagerType(entry))?.label || 'Bet'
  )
  const primaryEntry = entries[0]
  const primaryPrice = estimateParlayPrice(primaryEntry, validationResult?.outcomes)

  return (
    <section className={styles.wagerStructure} aria-labelledby="wager-structure-heading">
      <div className={styles.wagerStructureHeader}>
        <div>
          <h3 id="wager-structure-heading">Attached bet</h3>
          <p>
            {outcomeIds.length === 1
              ? 'One selection will be attached as a straight bet.'
              : `${outcomeIds.length} selections are attached as one parlay.`}
          </p>
        </div>
        <strong className={styles.wagerTypeBadge}>{entries.length === 1 ? wagerLabel(primaryEntry) : `${entries.length} bets`}</strong>
      </div>

      <ul className={styles.attachedSelectionList}>
        {outcomeIds.map((outcomeId) => {
          const detail = outcomeDetail(outcomeId)
          return (
            <li key={outcomeId}>
              <span className={styles.attachedSelectionCopy}>
                <strong>{detail.selection}</strong>
                {[detail.market, detail.event].filter(Boolean).length > 0 ? (
                  <span>{[detail.market, detail.event].filter(Boolean).join(' · ')}</span>
                ) : null}
              </span>
              <span className={styles.attachedSelectionActions}>
                {detail.odds ? <strong className={styles.attachedSelectionOdds}>{detail.odds}</strong> : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove ${outcomeLabel(outcomeId)}`}
                  onClick={() => onOutcomeIdsChange(outcomeIds.filter((id) => id !== outcomeId))}
                >
                  Remove
                </Button>
              </span>
            </li>
          )
        })}
      </ul>

      {primaryPrice && entries.length === 1 ? (
        <div className={styles.wagerPrice} aria-label={`Estimated parlay odds ${primaryPrice.american}`}>
          <span>Estimated parlay odds</span>
          <strong>{primaryPrice.american}</strong>
          <span>{primaryPrice.decimal} decimal</span>
        </div>
      ) : null}

      <details className={styles.wagerAdvanced}>
        <summary>Advanced wager setup</summary>
        <div className={styles.wagerAdvancedContent}>
          <div className={styles.wagerEntryActions}>
            <p>Change the bet type or split selections into multiple betslip entries.</p>
            <Button type="button" variant="secondary" size="sm" onClick={addWager}>
              Add bet
            </Button>
          </div>
          <div className={styles.wagerEntryList}>
            {entries.map((entry, entryIndex) => {
              const type = authoredWagerType(entry)
              const stake = entry.stakes?.[0]
              const parlayPrice = estimateParlayPrice(entry, validationResult?.outcomes)
              return (
                <fieldset className={styles.wagerEntry} key={entry.id}>
                  <legend>{wagerLabel(entry)}</legend>
                  <div className={styles.wagerEntryToolbar}>
                    <label>
                      Bet type
                      <select
                        value={type}
                        onChange={(event) => updateEntry(entryIndex, (current) => changeAuthoredWagerType(current, event.target.value))}
                      >
                        {KAMBI_WAGER_TYPES.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onChange(entries.filter((_, index) => index !== entryIndex))}
                    >
                      Remove
                    </Button>
                  </div>
                  <div className={styles.wagerOutcomes}>
                    <span>Selections in this bet</span>
                    {outcomeIds.map((outcomeId) => {
                      const normalizedID = String(outcomeId)
                      const checked = (entry.provider_outcome_ids || []).includes(normalizedID)
                      return (
                        <label key={normalizedID}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(event) => updateEntry(entryIndex, (current) => ({
                              ...current,
                              provider_outcome_ids: event.target.checked
                                ? [...(current.provider_outcome_ids || []), normalizedID]
                                : (current.provider_outcome_ids || []).filter((id) => id !== normalizedID),
                              stakes: [],
                            }))}
                          />
                          <span>{outcomeLabel(outcomeId)}</span>
                        </label>
                      )
                    })}
                  </div>
                  {parlayPrice ? (
                    <div className={styles.wagerPrice} aria-label={`Estimated parlay odds ${parlayPrice.american}`}>
                      <span>Estimated parlay odds</span>
                      <strong>{parlayPrice.american}</strong>
                      <span>{parlayPrice.decimal} decimal</span>
                    </div>
                  ) : null}
                  <details className={styles.wagerStake}>
                    <summary>Optional stake</summary>
                    <label>
                      Stake amount
                      <input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={stake ? stake / 1000 : ''}
                        onChange={(event) => {
                          const amount = Number(event.target.value)
                          updateEntry(entryIndex, (current) => ({
                            ...current,
                            stakes: event.target.value && Number.isFinite(amount) && amount > 0
                              ? [Math.round(amount * 1000)]
                              : [],
                          }))
                        }}
                        placeholder="Optional"
                      />
                    </label>
                    <p>Uses the account currency.</p>
                  </details>
                </fieldset>
              )
            })}
          </div>
        </div>
      </details>
      {validationError ? <p className={styles.wagerStructureError} role="alert">{validationError}</p> : null}
    </section>
  )
}

export default function BetSection({
  isKambi,
  isCryptoDotCom,
  isOG,
  outcomeIds,
  targetOfferings,
  allOfferings,
  providerId,
  destinations,
  onOutcomeIdsChange,
  onValidationResult,
  selectorRef,
  outcomeEntryDisclosure = false,
  hideOutcomeEntry = false,
  selectedCreator,
  // Legacy single-org callers (the org-scoped PostCreator/MarketStep tool)
  // still pass a plain `orgSlug`; Command Center passes `destinationOrganizations`
  // instead and never a slug.
  orgSlug,
  destinationOrganizations = [],
  selectionIds = [],
  onSelectionIdsChange,
  providerSelectionIds = [],
  onProviderSelectionIdsChange,
  onSelectedProviderSelectionsChange,
  extractBetDetailsAction = null,
  wagerEntries = [],
  onWagerEntriesChange = () => {},
  wagerValidationError = '',
  validationResult = null,
  embedded = false,
  // Command Center passes a destination-scoped leg search ({ scope, search,
  // offerings, query, onQueryChange, onSelect, onAddOutcomeId, enabled,
  // coverage, help }); the separate outcome-ID input then goes, as on the
  // Feed composer. Passed before a Kambi provider is picked, it renders
  // disabled.
  marketSearch = null,
  // Replaces the legacy selection-UUID input for callers that cannot attach
  // bets without a supported provider.
  unavailableBetMessage = '',
}) {
  const isPredictionProvider = isCryptoDotCom || isOG
  const hasDestinationContext = destinationOrganizations.length > 0
  // Crypto.com/OG lookup APIs are retired-provider tools that remain
  // org-scoped. This context never enters the mutation payload -- it only
  // picks which destination's search/lookup API to call. It auto-selects
  // when there is exactly one destination and otherwise requires an
  // explicit choice before any lookup fires; the server preflight remains
  // authoritative for what actually gets attached.
  const [marketLookupOrganizationId, setMarketLookupOrganizationId] = useState('')
  const resolvedMarketLookupOrganizationId = destinationOrganizations.length === 1
    ? destinationOrganizations[0].id
    : marketLookupOrganizationId
  const marketLookupOrganization = destinationOrganizations.find(
    (organization) => organization.id === resolvedMarketLookupOrganizationId,
  ) || null
  const marketLookupPending = hasDestinationContext && !marketLookupOrganization
  const marketLookupSlug = hasDestinationContext ? marketLookupOrganization?.slug : orgSlug
  const title = isPredictionProvider ? 'Contracts' : 'Bets'
  const description = isPredictionProvider
    ? `Choose one ${isOG ? 'OG' : 'Crypto.com'} prediction contract.`
    : 'Configure the wager for this post.'
  const extractLabel = isPredictionProvider ? 'Extract contract details' : 'Extract bet details'
  const marketSearchField = marketSearch ? (
    <LegSearch
      context="feed"
      scope={marketSearch.scope}
      search={marketSearch.search}
      offerings={marketSearch.offerings}
      query={marketSearch.query}
      onQueryChange={marketSearch.onQueryChange}
      selectedIds={outcomeIds}
      onSelect={marketSearch.onSelect}
      onAddOutcomeId={marketSearch.onAddOutcomeId}
      enabled={marketSearch.enabled}
      coverage={marketSearch.coverage}
      help={marketSearch.help}
      // Command Center is internal-only; the API still decides what donor
      // detail a borrowed result carries.
      role={ROLES.INTERNAL_ORG_ADMIN}
    />
  ) : null
  const content = (
    <>
      {isKambi ? (
        <>
          {marketSearchField}
          <WagerStructureEditor
            outcomeIds={outcomeIds}
            entries={wagerEntries}
            onChange={onWagerEntriesChange}
            onOutcomeIdsChange={onOutcomeIdsChange}
            validationResult={validationResult}
            validationError={wagerValidationError}
          />
          <KambiOutcomeSelector
            ref={selectorRef}
            providerId={providerId}
            destinations={destinations}
            allOfferings={allOfferings}
            outcomeIds={outcomeIds}
            targetOfferings={targetOfferings}
            onOutcomeIdsChange={onOutcomeIdsChange}
            onValidationResult={onValidationResult}
            outcomeEntryDisclosure={outcomeEntryDisclosure}
            hideOutcomeEntry={hideOutcomeEntry || Boolean(marketSearch)}
            hideValidationPreview={embedded}
          />
          {selectedCreator?.allowed_offerings?.length > 0 ? (
            <p className={styles.sectionHelper}>
              This creator is limited to {selectedCreator.allowed_offerings.join(', ')}. Restricted regulations stay disabled under Where it runs.
            </p>
          ) : null}
        </>
      ) : isPredictionProvider ? (
        <>
          {destinationOrganizations.length > 1 ? (
            <FormField label="Market lookup brand">
              <select
                value={marketLookupOrganizationId}
                onChange={(event) => setMarketLookupOrganizationId(event.target.value)}
                className={styles.select}
              >
                <option value="">Choose a brand to search {isOG ? 'OG' : 'Crypto.com'} markets</option>
                {destinationOrganizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>{organization.name}</option>
                ))}
              </select>
            </FormField>
          ) : null}
          {marketLookupPending ? (
            <p className={styles.sectionHelper}>
              Choose a market lookup brand to search {isOG ? 'OG' : 'Crypto.com'} contracts. Every destination still gets its own server-verified market data at publish time.
            </p>
          ) : isCryptoDotCom ? (
            <CryptoDotComContractSelector
              orgSlug={marketLookupSlug}
              providerSelectionIds={providerSelectionIds}
              onProviderSelectionIdsChange={onProviderSelectionIdsChange}
              onSelectedContractsChange={onSelectedProviderSelectionsChange}
            />
          ) : (
            <OGContractSelector
              orgSlug={marketLookupSlug}
              providerSelectionIds={providerSelectionIds}
              onProviderSelectionIdsChange={onProviderSelectionIdsChange}
              onSelectedContractsChange={onSelectedProviderSelectionsChange}
            />
          )}
        </>
      ) : marketSearchField ? (
        marketSearchField
      ) : unavailableBetMessage ? (
        <p className={styles.sectionHelper}>{unavailableBetMessage}</p>
      ) : (
        <ChipInput
          label="Selections"
          value={selectionIds}
          onChange={onSelectionIdsChange}
          placeholder="Enter selection UUID and press Enter"
          help="Add selection UUIDs for betting content"
        />
      )}
      {extractBetDetailsAction ? (
        <div className={styles.fieldActionRow}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={extractBetDetailsAction.onClick}
            disabled={extractBetDetailsAction.disabled}
          >
            {extractBetDetailsAction.pending ? 'Thinking...' : extractLabel}
          </Button>
          {extractBetDetailsAction.reason ? <span className={styles.fieldActionHint}>{extractBetDetailsAction.reason}</span> : null}
        </div>
      ) : null}
    </>
  )

  if (embedded) {
    return <div className={styles.sectionStack}>{content}</div>
  }

  return (
    <FormSection title={title} description={description} headingSize="subsection" contentClassName={styles.sectionStack}>
      {content}
    </FormSection>
  )
}
